import { z } from 'zod';

export const loginSchema = z.object({
  email: z.string().email().nonempty(),
  password: z.string().nonempty(),
});

export const signUpSchema = z.object({
  email: z.string().email().nonempty().toLowerCase(),
  password: z.string().nonempty(),
  firstName: z.string().nonempty(),
  lastName: z.string().nonempty(),
});

export const userSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  application: z.string(),
  privilege: z.string(),
  createdAt: z.instanceof(Date),
});

export const criteriaSchema = z.object({
  name: z.string(),
  weight: z.number(),
  points: z.number(),
  total: z.number(),
});

export const applicantSchema = z.object({
  gender: z.union([z.literal('male'), z.literal('female')], {
    error: 'Please select a gender option',
  }),
  firstName: z.string().trim().nonempty('The field First Name is required'),
  middleName: z.string().trim().optional(),
  surname: z.string().trim().nonempty('The field Surname is required'),
  email: z
    .string()
    .trim()
    .email('Please enter a valid email address')
    .nonempty('The Email field is required'),
  mailingAddress: z.string().trim().optional(),
  parish: z.string().trim().nonempty('The field parish is required'),
  homePhone: z.string().trim().optional(),
  nationality: z.string().trim().nonempty('The field nationality is required'),
  cellphone: z.string().trim().nonempty('The field cellphone is required'),
  birthDate: z.string().trim().nonempty('The field birthDate is required'),
  address: z.string().trim().nonempty('The field address is required'),
});

export const programSchema = z
  .object({
    institute: z.string().nonempty('The field institute is required'),
    duration: z.string().nonempty('The field duration is required'),
    gpa: z.string().nonempty('The field gpa is required'),
    tuition: z.string().nonempty('The field tuition is required'),
    major: z.string().nonempty('The field major is required'),
    details: z.string().default('N/A'),
    seniority: z.string().nonempty('The seniority major is required'),
    previousScholarship: z.union([z.literal('yes'), z.literal('no')], {
      error: 'Please select an option for previous scholarship',
    }),
  })
  .refine(
    (schema) =>
      schema.previousScholarship === 'yes' ? !!schema.details : true,
    {
      message: 'Please provide details about your previous scholarship.',
    }
  );

export const qualificationSchema = z.object({
  id: z.string().default(''),
  name: z.string().default(''),
  type: z.string().default(''),
  institution: z.string().default(''),
  duration: z.string().default(''),
  completed: z.string().default(''),
});

export const qualificationsSchema = z
  .array(qualificationSchema)
  .min(1, 'You must add at least one qualification');

export const educationSchema = z.object({
  qualifications: z.array(qualificationSchema),
  highSchool: z.string().nonempty('The field High School is required'),
  firstGeneration: z.union([z.literal('on'), z.literal('off')], {
    error: 'Please select if you are first generation',
  }),
  year: z.string().nonempty('The field Year is required'),
});

export const essaySchema = z.object({
  reason: z
    .string()
    .nonempty()
    .refine((str) => str.split(' ').length <= 500, {
      message: 'The essay must have 500 words as maximum',
    }),
  financial: z
    .string()
    .nonempty()
    .refine((str) => str.split(' ').length <= 500, {
      message: 'The financial essay must have 500 words as maximum',
    }),
});

export const personSchema = z.object({
  id: z.string().default(''),
  name: z.string().nonempty('The field name is required'),
  position: z.string().nonempty('The field position is required'),
  institution: z.string().nonempty('The field institution is required'),
  address: z.string().nonempty('The field address is required'),
  phone: z.string().nonempty('The field phone is required'),
});

export const peopleSchema = z.tuple([
  personSchema.strict(),
  z.object({
    id: z.string().default(''),
    name: z.string().default(''),
    position: z.string().default(''),
    institution: z.string().default(''),
    address: z.string().default(''),
    phone: z.string().default(''),
  }),
]);

export const serviceSchema = z.object({
  id: z.string().default(''),
  service: z.string().default(''),
  date: z.string().default(''),
});

export const officeSchema = z.object({
  id: z.string().default(''),
  name: z.string().default(''),
  year: z.string().default(''),
});

export const volunteeringSchema = z
  .object({
    resume: z.string().default(''),
    service: z.string().default(''),
    clubs: z.array(officeSchema).default([]),
    leadership: z.array(officeSchema).default([]),
    services: z.array(serviceSchema).default([]),
    voluntaryService: z.union([z.literal('yes'), z.literal('no')], {
      error: 'Please if you had done voluntary service',
    }),
  })
  .refine((schema) => {
    if (schema.voluntaryService === 'yes' && schema.services.length > 1) {
      return {
        message: 'You must add at least one voluntary service description',
      };
    }
    return { message: 'And error had occur' };
  });

export const categorySchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.instanceof(Date),
});

export const applicationSchema = z.object({
  id: z.string(),
  user: z.string(),
  startDate: z.instanceof(Date),
  lastUpdated: z.instanceof(Date),
  category: z.string(),
  education: educationSchema,
  other: z.string(),
  essay: essaySchema,
  step: z.string(),
  signature: z.string(),
  applicant: applicantSchema,
  hear: z.array(z.string()),
  program: programSchema,
  people: z.array(personSchema),
  volunteering: volunteeringSchema,
  status: z.string(),
  notes: z.string(),
  reviewer: z.string(),
  criteria: z.array(criteriaSchema),
  total: z.number().min(0),
});

export const assignmentSchema = z.object({
  id: z.string(),
  reviewer: userSchema,
  applications: z.array(applicationSchema),
});

export const contactFormSchema = z.object({
  name: z.string().nonempty('The field name is required'),
  email: z.string().email('The field email is required'),
  subject: z.string().nonempty('The field subject is required'),
  message: z.string().optional(),
});

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  application: string;
  privilege: string;
  createdAt: Date;
}

export interface Criteria {
  name: string;
  weight: number;
  points: number;
  total: number;
}

export interface Application {
  id: string;
  user: string;
  startDate: Date;
  lastUpdated: Date;
  category: string;
  education: Education;
  other: string;
  essay: Essay;
  step: string;
  signature: string;
  applicant: Applicant;
  hear: string[];
  program: Program;
  people: Person[];
  volunteering: Volunteering;
  status: string;
  notes: string;
  reviewer: string;
  criteria: Criteria[];
  total: number;
  archive: boolean;
}

export interface Education {
  qualifications: Qualification[];
  highSchool: string;
  firstGeneration: string;
  year: string;
}

export interface Qualification {
  id: string;
  name: string;
  type: string;
  institution: string;
  duration: string;
  completed: string;
}

export interface Office {
  id: string;
  name: string;
  year: string;
}

export interface Person {
  id: string;
  name: string;
  position: string;
  institution: string;
  address: string;
  phone: string;
}

export interface Essay {
  reason: string;
  financial: string;
}

export interface Applicant {
  gender: string;
  middleName: string;
  email: string;
  mailingAddress: string;
  surname: string;
  parish: string;
  firstName: string;
  homePhone: string;
  nationality: string;
  cellphone: string;
  birthDate: string;
  address: string;
  birthCertificatePassport: string;
}

export interface Program {
  previousScholarship: string;
  academicTranscript: string;
  institute: string;
  urlSchoolID: string;
  duration: string;
  gpa: string;
  tuition: string;
  major: string;
  details: string;
  seniority: string;
}

export interface Volunteering {
  resume: string;
  service: string;
  clubs: Office[];
  leadership: Office[];
  services: Service[];
  voluntaryService: string;
  date: string;
}

export interface Service {
  id: string;
  service: string;
  date: string;
}

export interface Category {
  id: string;
  name: string;
  createdAt: Date;
}

export interface Assignment {
  id: string;
  reviewer: User;
  applications: Application[];
}
